import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { BookOpen, ExternalLink, Search, Copy, Check } from 'lucide-react';
import { SNMP_OID_LIBRARY, EXTERNAL_OID_RESOURCES, getAllVendors } from '../data/snmpOidLibrary';
import { toast } from 'sonner';

const SNMPOidHelper = ({ onSelectOid }) => {
  const [selectedVendor, setSelectedVendor] = useState('generic');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedOid, setCopiedOid] = useState(null);

  const vendors = getAllVendors();
  const currentVendorData = SNMP_OID_LIBRARY[selectedVendor];

  const filteredOids = currentVendorData?.oids.filter(oid =>
    oid.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    oid.oid.includes(searchTerm) ||
    oid.description.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleCopyOid = (oid) => {
    navigator.clipboard.writeText(oid);
    setCopiedOid(oid);
    setTimeout(() => setCopiedOid(null), 2000);
    toast.success('OID copied to clipboard');
  };

  const handleSelectOid = (oid) => {
    onSelectOid(oid);
    toast.success(`OID selected: ${oid}`);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="w-full">
          <BookOpen className="w-4 h-4 mr-2" />
          OID Library & Help
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            SNMP OID Library
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Vendor Selection */}
          <div className="space-y-2">
            <Label>Select Device Vendor/Type</Label>
            <Select value={selectedVendor} onValueChange={setSelectedVendor}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {vendors.map(vendor => (
                  <SelectItem key={vendor.key} value={vendor.key}>
                    {vendor.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search */}
          <div className="space-y-2">
            <Label>Search OIDs</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, OID, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* OID List */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto border rounded-lg p-3">
            {filteredOids.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No OIDs found</p>
            ) : (
              filteredOids.map((oid, index) => (
                <div
                  key={index}
                  className="border border-slate-200 rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-800 mb-1">{oid.name}</h4>
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-700 font-mono">
                        {oid.oid}
                      </code>
                      <p className="text-sm text-slate-600 mt-2">{oid.description}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCopyOid(oid.oid)}
                        className="h-8 px-2"
                      >
                        {copiedOid === oid.oid ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleSelectOid(oid.oid)}
                        className="h-8"
                      >
                        Use This
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* External Resources */}
          <div className="border-t pt-4">
            <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              External OID Resources
            </h4>
            <div className="space-y-2">
              {EXTERNAL_OID_RESOURCES.map((resource, index) => (
                <a
                  key={index}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{resource.name}</p>
                      <p className="text-xs text-slate-600 mt-1">{resource.description}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400" />
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
            <h4 className="font-semibold text-blue-900 mb-2">💡 How to use:</h4>
            <ul className="text-blue-800 space-y-1 list-disc list-inside">
              <li>Select your device vendor/type from the dropdown</li>
              <li>Browse common OIDs for that device</li>
              <li>Click "Use This" to apply the OID to your monitor</li>
              <li>Or copy the OID and paste it manually</li>
              <li>For unlisted devices, check the external resources</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SNMPOidHelper;
